import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";

import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import DoneIcon from "@material-ui/icons/Done";
import UndoIcon from "@material-ui/icons/Undo";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import PersonIcon from "@material-ui/icons/PersonOutline";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import StatusChip from "../../components/StatusChip";
import useSocketSubscription from "../../hooks/useSocketSubscription";

export const KIND_META = {
  birthday: { label: "Aniversário", tone: "info" },
  renewal: { label: "Renovação", tone: "warning" },
  anniversary: { label: "Data comemorativa", tone: "info" },
  seasonal: { label: "Sazonal", tone: "neutral" },
  custom: { label: "Outra", tone: "neutral" },
};

/** De onde a data veio — o que explica por que umas dão para editar e outras não. */
const SOURCE_META = {
  manual: { label: "Cadastrada", tone: "neutral" },
  client_birthday: { label: "Do cadastro", tone: "info" },
  contract: { label: "Contrato", tone: "warning" },
  subscription: { label: "Assinatura", tone: "warning" },
};

const fmtDate = (d) =>
  d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString("pt-BR") : "—";

const useStyles = makeStyles((theme) => ({
  days: {
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    fontWeight: 700,
  },
  urgent: { color: theme.palette.error.main },
  soon: { color: theme.palette.warning.main },
  calm: { color: theme.palette.text.primary },
  date: { fontVariantNumeric: "tabular-nums", color: theme.palette.text.secondary },
  sub: { color: theme.palette.text.secondary, display: "block" },
  muted: { color: theme.palette.text.hint },
  handledRow: { opacity: 0.55 },
  chips: { display: "flex", gap: 4, flexWrap: "wrap" },
}));

const KeyDates = () => {
  const classes = useStyles();
  const history = useHistory();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("");
  // Abre olhando 90 dias, e não a antecedência estrita de cada data. Uma fila
  // vazia na primeira abertura é indistinguível de tela quebrada — foi
  // exatamente essa a impressão que a versão anterior deu.
  const [withinDays, setWithinDays] = useState("90");
  const [includeHandled, setIncludeHandled] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (kind) params.kind = kind;
      if (withinDays) params.withinDays = withinDays;
      if (includeHandled) params.includeHandled = true;
      const { data } = await api.get("/key-dates/queue", { params });
      setItems(data?.items || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [kind, withinDays, includeHandled]);

  useEffect(() => {
    load();
  }, [load]);

  useSocketSubscription("keyDate", load);

  const toggleHandled = async (item) => {
    try {
      await api.post(`/key-dates/${item.id}/handled`, { undo: item.handled });
      toast.success(item.handled ? "Voltou para a fila." : "Marcada como trabalhada.");
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const columns = [
    {
      key: "daysUntil",
      label: "Faltam",
      width: 110,
      render: (i) => {
        // Quantos dias faltam é o dado que faz alguém agir hoje. A data
        // sozinha obriga a fazer a conta de cabeça em cada linha.
        const cls =
          i.daysUntil <= 7
            ? classes.urgent
            : i.daysUntil <= 30
            ? classes.soon
            : classes.calm;
        return (
          <span className={`${classes.days} ${cls}`}>
            {i.daysUntil === 0 ? "hoje" : `${i.daysUntil} dias`}
          </span>
        );
      },
    },
    {
      key: "label",
      label: "O que é",
      render: (i) => (
        <div>
          <Typography variant="subtitle2">
            {i.label}
            {i.turningYears ? (
              <>
                {" "}
                <StatusChip tone="success" label={`faz ${i.turningYears} ano${i.turningYears > 1 ? "s" : ""}`} />
              </>
            ) : null}
          </Typography>
          {i.notes && (
            <Typography variant="caption" className={classes.sub}>
              {i.notes}
            </Typography>
          )}
        </div>
      ),
    },
    {
      key: "kind",
      label: "Tipo",
      render: (i) => {
        const meta = KIND_META[i.kind] || { label: i.kind, tone: "neutral" };
        const src = SOURCE_META[i.source] || SOURCE_META.manual;
        return (
          <div className={classes.chips}>
            <StatusChip dot tone={meta.tone} label={meta.label} />
            <StatusChip tone={src.tone} label={src.label} />
          </div>
        );
      },
    },
    {
      key: "client",
      label: "Cliente",
      render: (i) => (
        <div>
          <Typography variant="subtitle2" noWrap>{i.clientName}</Typography>
          {i.clientPhone && (
            <Typography variant="caption" className={classes.sub}>
              {i.clientPhone}
            </Typography>
          )}
        </div>
      ),
    },
    {
      key: "occurrence",
      label: "Data",
      render: (i) => <span className={classes.date}>{fmtDate(i.occurrence)}</span>,
    },
    {
      key: "actions",
      label: "Ações",
      type: "actions",
      render: (i) => (
        <>
          <Tooltip title="Abrir cliente">
            <IconButton
              size="small"
              onClick={() => history.push(`/clients/${i.clientId}`)}
            >
              <PersonIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {i.clientPhone && (
            <Tooltip title="Abrir conversa no WhatsApp">
              <IconButton
                size="small"
                onClick={() =>
                  window.open(
                    `https://wa.me/${String(i.clientPhone).replace(/\D/g, "")}`,
                    "_blank",
                    "noopener"
                  )
                }
              >
                <WhatsAppIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {/* Derivadas não têm registro próprio para guardar "já trabalhei
              esta". Só as cadastradas à mão podem ser marcadas. */}
          {i.id ? (
            <Tooltip title={i.handled ? "Voltar para a fila" : "Marcar como trabalhada"}>
              <IconButton size="small" onClick={() => toggleHandled(i)}>
                {i.handled ? <UndoIcon fontSize="small" /> : <DoneIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Vem do cadastro do cliente, do contrato ou da assinatura — some da fila quando a data passar">
              <span className={classes.muted}>automática</span>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  return (
    <MainContainer>
      <PageHeader
        title="Datas a trabalhar"
        count={items.length}
        subtitle="Tudo que tem data chegando: aniversário do cadastro do cliente, vencimento de contrato, cobrança de assinatura e as datas cadastradas à mão."
        filters={
          <>
            <TextField
              select
              label="Tipo"
              style={{ minWidth: 180 }}
              value={kind}
              onChange={(e) => setKind(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(KIND_META).map(([value, meta]) => (
                <MenuItem key={value} value={value}>
                  {meta.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Olhar até"
              style={{ minWidth: 200 }}
              value={withinDays}
              onChange={(e) => setWithinDays(e.target.value)}
              helperText="Vazio mostra só o que entrou na antecedência configurada"
            >
              <MenuItem value="">Só o que já pede ação</MenuItem>
              <MenuItem value="30">Próximos 30 dias</MenuItem>
              <MenuItem value="60">Próximos 60 dias</MenuItem>
              <MenuItem value="90">Próximos 90 dias</MenuItem>
              <MenuItem value="180">Próximos 6 meses</MenuItem>
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={includeHandled}
                  onChange={(e) => setIncludeHandled(e.target.checked)}
                />
              }
              label="Mostrar já trabalhadas"
            />
          </>
        }
      />

      <DataTable
        columns={columns}
        rows={items}
        loading={loading}
        getRowKey={(i) => i.sourceKey || `manual:${i.id}`}
        emptyIcon={<EventAvailableIcon />}
        emptyTitle="Nada chegando por enquanto"
        emptyDescription="Preencha a data de nascimento no cadastro do cliente, ou cadastre datas na aba Datas da ficha — aniversário de uma criança, vencimento de plano. Elas aparecem aqui com antecedência, todo ano."
      />
    </MainContainer>
  );
};

export default KeyDates;
